import { useState, useRef, useCallback } from 'react'

/**
 * Multi-select hook with shift-click range support.
 *
 * Usage:
 *   const { selected, isSelecting, toggleSelect, selectRange, clear, lastSelectedIdx } = useMultiSelect()
 *
 * - toggleSelect(id, idx): toggles a single item. If shift+click, selects range from last to idx.
 * - clear(): clears all selections.
 * - selected: Set of selected IDs.
 * - isSelecting: boolean — true when in selection mode (at least one selected).
 */
export function useMultiSelect() {
  const [selected, setSelected] = useState(new Set<string>())
  const lastSelectedIdx = useRef<number | null>(null)

  const toggleSelect = useCallback((id: string, idx: number, shiftKey = false) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (shiftKey && lastSelectedIdx.current !== null && lastSelectedIdx.current !== idx) {
        // Range select — add all items between last and current
        const start = Math.min(lastSelectedIdx.current, idx)
        const end = Math.max(lastSelectedIdx.current, idx)
        // We need the full list to do range select — caller provides IDs via selectRange
        return next // handled by selectRange instead
      }
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      lastSelectedIdx.current = idx
      return next
    })
  }, [])

  const selectRange = useCallback((ids: string[], fromIdx: number, toIdx: number) => {
    setSelected(prev => {
      const next = new Set(prev)
      const start = Math.min(fromIdx, toIdx)
      const end = Math.max(fromIdx, toIdx)
      for (let i = start; i <= end; i++) {
        if (ids[i]) next.add(ids[i])
      }
      lastSelectedIdx.current = toIdx
      return next
    })
  }, [])

  const clear = useCallback(() => {
    setSelected(new Set<string>())
    lastSelectedIdx.current = null
  }, [])

  const isSelecting = selected.size > 0

  return { selected, isSelecting, toggleSelect, selectRange, clear, lastSelectedIdx }
}
